import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdAdd, MdDelete, MdSchedule, MdCalendarToday } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getSchedules, createSchedule, deleteSchedule, getVideos, getChannels } from '../utils/api';

const statusColors = {
  pending: 'badge-warning',
  published: 'badge-success',
  failed: 'badge-danger'
};

const platformIcons = {
  youtube: <FaYoutube style={{ color: '#ff0000', fontSize: '16px' }} />,
  facebook: <FaFacebook style={{ color: '#1877f2', fontSize: '16px' }} />,
  tiktok: <FaTiktok style={{ color: '#000000', fontSize: '16px' }} />,
  instagram: <FaInstagram style={{ color: '#e1306c', fontSize: '16px' }} />
};

const emptyForm = {
  video_id: '',
  channel_id: '',
  scheduled_time: '',
  platform: 'youtube',
  publish_mode: 'instant_auto'
};

const ScheduleManager = () => {
  const [schedules, setSchedules] = useState([]);
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [scheduleVideoMode, setScheduleVideoMode] = useState('library');
  const [scheduleLocalFile, setScheduleLocalFile] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, videosRes, channelsRes] = await Promise.all([
        getSchedules(),
        getVideos(),
        getChannels()
      ]);
      setSchedules(schedulesRes.data.data || []);
      setVideos(videosRes.data.data || []);
      setChannels(channelsRes.data.data || []);
    } catch (error) {
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.video_id || !form.channel_id || !form.scheduled_time) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await createSchedule(form);
      toast.success('Schedule created successfully');
      setShowModal(false);
      setForm(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await deleteSchedule(id);
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const getVideoTitle = (videoId) => {
    const video = videos.find(v => v.id === parseInt(videoId));
    return video ? (video.youtube_title || video.title || `Video #${videoId}`) : `Video #${videoId}`;
  };

  const getChannelName = (channelId) => {
    const channel = channels.find(c => c.id === parseInt(channelId));
    return channel ? channel.name : `Channel #${channelId}`;
  };

  const filteredSchedules = schedules.filter(s => {
    const matchStatus = filterStatus ? s.status === filterStatus : true;
    const matchPlatform = filterPlatform ? s.platform === filterPlatform : true;
    return matchStatus && matchPlatform;
  });

  const upcoming = filteredSchedules.filter(s =>
    s.status === 'pending' && new Date(s.scheduled_time) >= new Date()
  );
  const past = filteredSchedules.filter(s =>
    s.status !== 'pending' || new Date(s.scheduled_time) < new Date()
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading"><div className="spinner"></div>Loading schedules...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Schedule Manager</h1>
            <p>Schedule your videos to publish automatically across platforms</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <MdAdd /> New Schedule
          </button>
        </div>
      </div>

      <div className="grid-3" style={{ marginBottom: '24px' }}>
        <div className="stats-card">
          <div className="stats-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <MdSchedule />
          </div>
          <div className="stats-info">
            <h3>{schedules.filter(s => s.status === 'pending').length}</h3>
            <p>Pending</p>
          </div>
        </div>
        <div className="stats-card">
          <div className="stats-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
            <MdCalendarToday />
          </div>
          <div className="stats-info">
            <h3>{schedules.filter(s => s.status === 'published').length}</h3>
            <p>Published</p>
          </div>
        </div>
        <div className="stats-card">
          <div className="stats-icon" style={{ background: '#fee2e2', color: '#ef4444' }}>
            <MdSchedule />
          </div>
          <div className="stats-info">
            <h3>{schedules.filter(s => s.status === 'failed').length}</h3>
            <p>Failed</p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <select
            className="form-select"
            style={{ width: '160px' }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="published">Published</option>
            <option value="failed">Failed</option>
          </select>
          <select
            className="form-select"
            style={{ width: '160px' }}
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
          >
            <option value="">All Platforms</option>
            <option value="youtube">YouTube</option>
            <option value="facebook">Facebook</option>
            <option value="tiktok">TikTok</option>
            <option value="instagram">Instagram</option>
          </select>
        </div>
      </div>

      {upcoming.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <span className="card-title">📅 Upcoming Schedules</span>
            <span className="badge badge-warning">{upcoming.length} pending</span>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Video</th>
                  <th>Channel</th>
                  <th>Platform</th>
                  <th>Scheduled Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map((schedule) => (
                  <tr key={schedule.id}>
                    <td style={{ fontWeight: '500', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getVideoTitle(schedule.video_id)}
                    </td>
                    <td style={{ fontSize: '13px' }}>{getChannelName(schedule.channel_id)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {platformIcons[schedule.platform]}
                        <span style={{ fontSize: '13px', textTransform: 'capitalize' }}>
                          {schedule.platform}
                        </span>
                      </div>
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {new Date(schedule.scheduled_time).toLocaleString()}
                    </td>
                    <td>
                      <span className={`badge ${statusColors[schedule.status]}`}>
                        {schedule.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(schedule.id)}
                      >
                        <MdDelete />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">📋 Past Schedules</span>
            <span className="badge badge-secondary">{past.length} records</span>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Video</th>
                  <th>Channel</th>
                  <th>Platform</th>
                  <th>Scheduled Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {past.map((schedule) => (
                  <tr key={schedule.id}>
                    <td style={{ fontWeight: '500', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getVideoTitle(schedule.video_id)}
                    </td>
                    <td style={{ fontSize: '13px' }}>{getChannelName(schedule.channel_id)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {platformIcons[schedule.platform]}
                        <span style={{ fontSize: '13px', textTransform: 'capitalize' }}>
                          {schedule.platform}
                        </span>
                      </div>
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {new Date(schedule.scheduled_time).toLocaleString()}
                    </td>
                    <td>
                      <span className={`badge ${statusColors[schedule.status]}`}>
                        {schedule.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(schedule.id)}
                      >
                        <MdDelete />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredSchedules.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <MdSchedule style={{ fontSize: '48px', color: '#c7d2fe', marginBottom: '12px' }} />
            <h3>No schedules yet</h3>
            <p>Create your first schedule to automate publishing</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <MdAdd /> Create Schedule
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create New Schedule</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="form-group">
              <label className="form-label">Select Video *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
  <div style={{ display: 'flex', gap: '8px' }}>
    <button
      className={`btn btn-sm ${scheduleVideoMode === 'library' ? 'btn-primary' : 'btn-secondary'}`}
      onClick={() => setScheduleVideoMode('library')}>
      📚 From Library
    </button>
    <button
      className={`btn btn-sm ${scheduleVideoMode === 'local' ? 'btn-primary' : 'btn-secondary'}`}
      onClick={() => setScheduleVideoMode('local')}>
      💻 From Computer
    </button>
  </div>
  {scheduleVideoMode === 'library' ? (
    <select
      className="form-select"
      value={form.video_id}
      onChange={(e) => setForm({ ...form, video_id: e.target.value })}
    >
      <option value="">Select a video...</option>
      {videos.map((v) => (
        <option key={v.id} value={v.id}>
          {v.youtube_title || v.title || `Video #${v.id}`}
        </option>
      ))}
    </select>
  ) : (
    <div>
      <input
        type="file"
        accept="video/*"
        className="form-control"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            setScheduleLocalFile(file);
            setForm({ ...form, video_id: 'local_' + file.name });
            toast.success('Video selected: ' + file.name);
          }
        }}
      />
      {scheduleLocalFile && (
        <div style={{
          marginTop: '8px', padding: '8px 12px', borderRadius: '8px',
          background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.2)',
          fontSize: '12px', color: 'var(--primary)', fontWeight: '600'
        }}>
          ✅ {scheduleLocalFile.name} ({(scheduleLocalFile.size / 1024 / 1024).toFixed(2)} MB)
        </div>
      )}
    </div>
  )}
</div>
            </div>

            <div className="form-group">
              <label className="form-label">Select Channel *</label>
              <select
                className="form-select"
                value={form.channel_id}
                onChange={(e) => setForm({ ...form, channel_id: e.target.value })}
              >
                <option value="">Select a channel...</option>
                {channels.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    {ch.name} ({ch.platform})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Platform *</label>
              <select
                className="form-select"
                value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })}
              >
                <option value="youtube">YouTube</option>
                <option value="facebook">Facebook</option>
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Publish Mode *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { id: 'manual', label: '👤 Manual Review', desc: 'AI generates 5 options — you pick one before publishing' },
                  { id: 'obsidian_suggestions', label: '🤖 Obsidian Suggestions', desc: 'AI picks best option automatically at scheduled time' },
                  { id: 'instant_auto', label: '⚡ Instant Auto', desc: 'AI generates everything including thumbnail and publishes instantly' }
                ].map(mode => (
                  <div key={mode.id}
                    onClick={() => setForm({ ...form, publish_mode: mode.id })}
                    style={{
                      padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                      background: form.publish_mode === mode.id ? 'rgba(0,200,83,0.08)' : 'var(--bg-dark)',
                      border: '1px solid ' + (form.publish_mode === mode.id ? 'rgba(0,200,83,0.4)' : 'var(--border)'),
                      transition: 'all 0.2s ease'
                    }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '2px' }}>
                      {mode.label}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{mode.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Scheduled Date & Time *</label>
              <input
                className="form-control"
                type="datetime-local"
                value={form.scheduled_time}
                onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : 'Create Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;